/**
 * Formats a version number as a 3-digit zero-padded string.
 * @param {string|number} version
 * @returns {string} e.g. "001", "012", "100"
 */
export const formatVersion = (version) => {
    const num = parseInt(version, 10);
    if (isNaN(num) || num < 0) return '001';
    return String(num).padStart(3, '0');
};

/**
 * Increments a 3-digit version string by 1.
 * @param {string} version e.g. "001"
 * @returns {string} e.g. "002"
 */
export const incrementVersion = (version) => {
    const num = parseInt(version, 10);
    if (isNaN(num) || num < 0) return '002';
    return String(num + 1).padStart(3, '0');
};

/**
 * Builds the display name from session metadata fields.
 * Format: "{Letter}{Number} - {Session Name} - v{Version}"
 * @param {object} metadata
 * @returns {string}
 */
export const buildExportDisplayName = ({ assignmentLetter, assignmentNumber, sessionName, version }) => {
    const letter = assignmentLetter || 'A';
    const number = assignmentNumber ?? 1;
    const name = sessionName || 'Untitled';
    const ver = formatVersion(version);
    return `${letter}${number} - ${name} - v${ver}`;
};

/**
 * Parses a legacy display name string like "A1 - Biped Jump - v001" into metadata fields.
 * Returns null if parsing fails.
 * @param {string} displayName
 * @returns {object|null}
 */
export const parseLegacyDisplayName = (displayName) => {
    if (!displayName || typeof displayName !== 'string') return null;
    // Match: Letter + Number + " - " + Name + " - v" + digits
    const match = displayName.match(/^([A-J])(\d+)\s*-\s*(.+?)\s*-\s*v(\d{1,3})$/i);
    if (!match) return null;
    return {
        assignmentLetter: match[1].toUpperCase(),
        assignmentNumber: parseInt(match[2], 10),
        sessionName: match[3].trim(),
        version: formatVersion(match[4])
    };
};

export const buildSessionExportData = ({
    sharedRubric,
    sharedCourseDetails,
    gradingFormData,
    classList,
    currentStudent,
    gradingSession,
    rubricFormData,
    aiPromptFormData,
    assignmentPromptFormData,
    drafts,
    finalGrades,
    courseLatePolicies,
    currentLatePolicy,
    customLatePolicies,
    gradingResetToken,
    gradeBook,
    activeTab,
    sessionExpirationEnabled,
    sessionMetadata,
    exportedAt = new Date().toISOString(),
    version = '2.0'
}) => {
    const metadata = sessionMetadata || {};
    const exportDisplayName = buildExportDisplayName(metadata);

    return {
        rubric: sharedRubric,
        courseDetails: sharedCourseDetails,
        gradingData: gradingFormData,
        classList,
        currentStudent,
        gradingSession,
        rubricFormData,
        aiPromptFormData,
        assignmentPromptFormData,
        drafts,
        finalGrades,
        courseLatePolicies,
        currentLatePolicy,
        customLatePolicies,
        gradingResetToken,
        gradeBook,
        activeTab,
        sessionExpirationEnabled,
        // Session naming & versioning metadata
        assignmentLetter: metadata.assignmentLetter || 'A',
        assignmentNumber: metadata.assignmentNumber ?? 1,
        sessionName: metadata.sessionName || '',
        sessionVersion: formatVersion(metadata.version),
        exportDisplayName,
        exportedAt,
        version,
        sessionExport: true
    };
};

export const normalizeSessionImportData = (sessionData) => {
    const data = typeof sessionData === 'string' ? JSON.parse(sessionData) : sessionData;

    // Try to recover session metadata from the JSON
    let sessionMetadata = null;

    if (data?.assignmentLetter || data?.sessionVersion || data?.sessionName) {
        // New format: explicit fields
        sessionMetadata = {
            assignmentLetter: data.assignmentLetter || 'A',
            assignmentNumber: data.assignmentNumber ?? 1,
            sessionName: data.sessionName || '',
            version: formatVersion(data.sessionVersion)
        };
    } else if (data?.exportDisplayName) {
        // Try parsing from exportDisplayName
        sessionMetadata = parseLegacyDisplayName(data.exportDisplayName);
    }

    // If still null, no session metadata to restore (legacy file without naming)
    // The caller can decide how to handle this (use defaults)

    return {
        rubric: data?.rubric,
        courseDetails: data?.courseDetails,
        gradingData: data?.gradingData,
        classList: data?.classList,
        currentStudent: data?.currentStudent,
        gradingSession: data?.gradingSession,
        rubricFormData: data?.rubricFormData,
        aiPromptFormData: data?.aiPromptFormData,
        assignmentPromptFormData: data?.assignmentPromptFormData,
        drafts: data?.drafts,
        finalGrades: data?.finalGrades,
        courseLatePolicies: data?.courseLatePolicies,
        currentLatePolicy: data?.currentLatePolicy,
        customLatePolicies: data?.customLatePolicies,
        gradingResetToken: typeof data?.gradingResetToken === 'number' ? data.gradingResetToken : undefined,
        gradeBook: data?.gradeBook,
        activeTab: data?.activeTab,
        sessionExpirationEnabled:
            typeof data?.sessionExpirationEnabled === 'boolean' ? data.sessionExpirationEnabled : undefined,
        sessionMetadata
    };
};
